import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Minus, Plus, ShoppingBag, Check } from "lucide-react";
import Seo from "@/components/Seo";
import {
  shopifyFetch,
  PRODUCT_BY_HANDLE,
  COLLECTION_BY_HANDLE,
  nodes,
  formatMoney,
} from "@/lib/shopify";
import { useCart } from "@/context/CartContext";
import { Eyebrow } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

export default function ProductDetailPage() {
  const { handle } = useParams();
  const { addToCart, busy } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [state, setState] = useState("loading");
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setSelectedImage(0);
    setQty(1);
    shopifyFetch(PRODUCT_BY_HANDLE, { handle })
      .then(async (d) => {
        if (cancelled) return;
        if (!d.product) {
          setState("notfound");
          return;
        }
        setProduct(d.product);
        const defaults = {};
        (d.product.options || []).forEach((o) => {
          if (o.values?.length) defaults[o.name] = o.values[0];
        });
        setSelectedOptions(defaults);
        setState("ready");
        const coll = nodes(d.product.collections)[0];
        if (coll) {
          try {
            const cd = await shopifyFetch(COLLECTION_BY_HANDLE, { handle: coll.handle, first: 5 });
            if (!cancelled && cd.collection) {
              setRelated(nodes(cd.collection.products).filter((p) => p.handle !== handle).slice(0, 4));
            }
          } catch {}
        }
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
    };
  }, [handle]);

  const variants = useMemo(() => (product ? nodes(product.variants) : []), [product]);
  const images = useMemo(() => (product ? nodes(product.images) : []), [product]);

  const currentVariant = useMemo(() => {
    if (!variants.length) return null;
    return (
      variants.find((v) =>
        v.selectedOptions.every((so) => selectedOptions[so.name] === so.value)
      ) || variants[0]
    );
  }, [variants, selectedOptions]);

  const onSale =
    currentVariant?.compareAtPrice &&
    parseFloat(currentVariant.compareAtPrice.amount) > parseFloat(currentVariant.price.amount);

  const handleAdd = async () => {
    if (!currentVariant) return;
    await addToCart(currentVariant.id, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (state === "loading")
    return (
      <main className="flex min-h-screen items-center justify-center bg-void pt-24" data-testid="pdp-loading">
        <Loader2 className="h-6 w-6 animate-spin text-amber" />
      </main>
    );

  if (state === "notfound" || state === "error")
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-void pt-24" data-testid="pdp-not-found">
        <p className="font-editorial text-3xl italic text-ivory/70">
          {state === "error" ? "The shop is ticking… try again shortly." : "This object escaped the shelf."}
        </p>
        <Link to="/shop" className="font-mono2 text-xs uppercase tracking-[0.2em] text-amber hover:underline">
          Back to the catalogue
        </Link>
      </main>
    );

  return (
    <main data-testid="product-detail-page" className="bg-void pt-28 sm:pt-32">
      <Seo
        title={`${product.title} — Weird O'Clock`}
        siteName="Weird O'Clock"
        description={product.description?.slice(0, 160)}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.title,
          image: images[0]?.url,
          description: product.description,
          offers: currentVariant
            ? {
                "@type": "Offer",
                price: currentVariant.price.amount,
                priceCurrency: currentVariant.price.currencyCode,
                availability: currentVariant.availableForSale
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
              }
            : undefined,
        }}
      />
      <div className="mx-auto max-w-[1400px] px-5 pb-24 sm:px-8">
        <nav className="mb-8 font-mono2 text-[10px] uppercase tracking-[0.25em] text-ivory/40" data-testid="pdp-breadcrumbs">
          <Link to="/shop" className="hover:text-amber">Shop</Link>
          <span className="mx-2">/</span>
          <span className="text-amber">{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div>
            <motion.div
              key={selectedImage}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative aspect-square overflow-hidden rounded-2xl border border-white/10 bg-obsidian"
            >
              {images[selectedImage]?.url ? (
                <img
                  src={images[selectedImage].url}
                  alt={images[selectedImage].altText || product.title}
                  className="h-full w-full object-cover"
                  data-testid="pdp-main-image"
                />
              ) : (
                <div className="flex h-full items-center justify-center font-mono2 text-xs text-ivory/30">
                  NO IMAGE
                </div>
              )}
              {onSale && (
                <span className="absolute left-4 top-4 rounded-full bg-amber px-3 py-1 font-mono2 text-[10px] font-bold uppercase tracking-widest text-void">
                  On sale
                </span>
              )}
            </motion.div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-2" data-testid="pdp-thumbnails">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`pdp-thumbnail-${i}`}
                    onClick={() => setSelectedImage(i)}
                    className={`h-20 w-20 shrink-0 overflow-hidden rounded-lg border transition-all ${
                      i === selectedImage ? "border-amber" : "border-white/10 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img.url} alt={img.altText || `${product.title} view ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Eyebrow>{product.vendor || "Weird O'Clock"}</Eyebrow>
            <h1
              data-testid="pdp-title"
              className="font-display text-3xl font-black tracking-tight text-ivory sm:text-4xl lg:text-5xl"
            >
              {product.title}
            </h1>

            <div className="mt-5 flex items-baseline gap-3" data-testid="pdp-price">
              <span className="font-mono2 text-2xl font-bold text-amber">
                {formatMoney(currentVariant?.price || product.priceRange.minVariantPrice)}
              </span>
              {onSale && (
                <span className="font-mono2 text-lg text-ivory/40 line-through">
                  {formatMoney(currentVariant.compareAtPrice)}
                </span>
              )}
            </div>

            {(product.options || [])
              .filter((o) => o.name !== "Title" && o.values?.length > 0)
              .map((option) => (
                <div key={option.name} className="mt-7" data-testid={`pdp-option-${option.name.toLowerCase()}`}>
                  <p className="mb-3 font-mono2 text-[10px] uppercase tracking-[0.25em] text-ivory/50">
                    {option.name} — <span className="text-amber">{selectedOptions[option.name]}</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {option.values.map((val) => (
                      <button
                        key={val}
                        data-testid={`pdp-option-${option.name.toLowerCase()}-${val.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setSelectedOptions((s) => ({ ...s, [option.name]: val }))}
                        className={`rounded-full border px-4 py-2 font-mono2 text-xs uppercase tracking-widest transition-all ${
                          selectedOptions[option.name] === val
                            ? "border-amber bg-amber/10 text-amber"
                            : "border-white/15 text-ivory/60 hover:border-ivory/40"
                        }`}
                      >
                        {val}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center rounded-full border border-white/15" data-testid="pdp-quantity-stepper">
                <button
                  data-testid="pdp-qty-minus"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="p-3 text-ivory/60 hover:text-amber"
                  aria-label="Decrease quantity"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-8 text-center font-mono2 text-sm font-bold text-ivory">{qty}</span>
                <button
                  data-testid="pdp-qty-plus"
                  onClick={() => setQty((q) => q + 1)}
                  className="p-3 text-ivory/60 hover:text-amber"
                  aria-label="Increase quantity"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                data-testid="pdp-add-to-cart-button"
                onClick={handleAdd}
                disabled={busy || !currentVariant?.availableForSale}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-amber py-4 font-mono2 text-xs font-bold uppercase tracking-[0.2em] text-void transition-all hover:shadow-[0_0_40px_rgba(255,92,0,0.35)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {added ? (
                  <>
                    <Check className="h-4 w-4" /> Added
                  </>
                ) : currentVariant?.availableForSale ? (
                  <>
                    <ShoppingBag className="h-4 w-4" /> Add to cart
                  </>
                ) : (
                  "Sold out"
                )}
              </button>
            </div>

            {product.description && (
              <div
                data-testid="pdp-description"
                className="mt-10 whitespace-pre-line border-t border-white/10 pt-8 text-base leading-relaxed text-ivory/60"
              >
                {product.description}
              </div>
            )}
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24" data-testid="pdp-related">
            <Eyebrow>Keep the weird going</Eyebrow>
            <h2 className="mb-10 font-display text-2xl font-extrabold tracking-tight text-ivory sm:text-3xl">
              You might also <span className="font-editorial italic text-amber">obsess over</span>
            </h2>
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-4">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
